import { InterviewKind } from '@Domain/Enums/Interviews/InterviewKind';
import type { CaptureChunk, CaptureFailure, CaptureSnapshot } from '@Domain/Ports/Capture/CapturePort';
import type { LocalInterview } from '@Domain/Ports/LocalInterviews/LocalInterviewPort';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import localInterviewCaptureState from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList/localInterviewCaptureState';
import useLiveTranscription from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/Creations/RecordInterviewPage/Hooks/useLiveTranscription';
import useLocalInterviews from '@UI/Pages/Beneficiaries/Hooks/useLocalInterviews';
import CAPTURE_TIMESLICE_MS from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/Shared/captureTimesliceMs';
import useCapturePort from '@UI/Providers/PortsProvider/Hooks/Capture/useCapturePort';
import useLocalBufferPort from '@UI/Providers/PortsProvider/Hooks/LocalBuffer/useLocalBufferPort';
import useLocalInterviewPort from '@UI/Providers/PortsProvider/Hooks/LocalInterviews/useLocalInterviewPort';
import useOutboxPort from '@UI/Providers/PortsProvider/Hooks/OutboxRunner/useOutboxPort';
import useScreenWakePort from '@UI/Providers/PortsProvider/Hooks/ScreenWake/useScreenWakePort';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@verclam/toast-v2';

/** Bounds a segment under the server's per-segment limit — voice in Opus runs roughly 10-15 MB/hour,
 * so five minutes stays far under it. */
const SEGMENT_ROTATION_MS = 300_000;
const ELAPSED_TICK_MS = 250;

const ITEM_PREPARATION_FAILURE_MESSAGE = "L'enregistrement n'a pas pu être préparé pour le transfert.";

type SessionPhase =
	| { readonly phase: 'checking-permission' }
	| { readonly phase: 'permission-denied' }
	| { readonly phase: 'unsupported' }
	| { readonly phase: 'ready' }
	| { readonly phase: 'starting' }
	| { readonly phase: 'format-unrecognized' }
	| { readonly phase: 'terminal-failure'; readonly reason: string }
	| { readonly phase: 'stopping' }
	| {
			readonly phase: 'recording' | 'paused';
			readonly recordedMs: number;
			readonly level: number;
			readonly interruptionCount: number;
	  };

export type InterviewCaptureScreenState =
	| { readonly phase: 'loading-interview' }
	| { readonly phase: 'interview-not-found' }
	| { readonly phase: 'already-captured' }
	| SessionPhase;

export type UseInterviewCaptureParams = {
	readonly localInterviewId: string;
	/** `null` while the session is still resolving: `start()` refuses to run before it is known,
	 * since the engine must be started with this exact id (`OutboxPort.ts`, `createItem` throws on a
	 * mismatch). */
	readonly ownerUserId: number | null;
};

export type UseInterviewCaptureResult = {
	readonly state: InterviewCaptureScreenState;
	/** `null` until the local interview is found. */
	readonly kind: InterviewKind | null;
	/** The preparation the interview is captured for; `null` until resolved, or when it has none. */
	readonly preparationId: number | null;
	readonly start: () => void;
	readonly pause: () => void;
	readonly resume: () => void;
	readonly stop: () => void;
};

const mapFailureToMessage = (failure: CaptureFailure): string => {
	switch (failure.reason) {
		case 'permission-denied':
			return "L'accès au microphone a été refusé.";
		case 'no-microphone':
			return "Aucun microphone n'a été détecté sur cet appareil.";
		case 'unsupported':
			return "Cet appareil ne prend pas en charge l'enregistrement audio.";
		case 'unrecoverable':
			return failure.detail;
		default:
			return 'Une erreur inattendue a interrompu la capture.';
	}
};

/**
 * Orchestrates V-19's capture session end to end (§8 of the spec, ten steps). Every side effect on
 * the terminal goes through a port — `CapturePort`, `OutboxPort`, `LocalBufferPort`, `ScreenWakePort`,
 * `LocalInterviewPort` — never `MediaRecorder`/`indexedDB`/`navigator` directly.
 *
 * Five points a reviewer cannot verify by reading the return value alone, all documented in the
 * completion summary: the in-memory queue of chunks captured before `itemId` exists (step 5), the
 * double guard on segment rotation (step 7), the pause-intent guard against the adapter restarting
 * the recorder on its own (step 8), the read-then-stop-then-markReady order at step 9, and the
 * already-captured race guard re-checked inside `start()` itself — necessary because the device this
 * screen runs on is shared, and `'ready'` may have been reached before another session attached a
 * queue item to the same local interview.
 */
function useInterviewCapture(params: UseInterviewCaptureParams): UseInterviewCaptureResult {
	const { localInterviewId, ownerUserId } = params;

	const navigate = useNavigate();
	const toast = useToast();
	const capturePort = useCapturePort();
	const localBufferPort = useLocalBufferPort();
	const outboxPort = useOutboxPort();
	const screenWakePort = useScreenWakePort();
	const localInterviewPort = useLocalInterviewPort();
	const localInterviews = useLocalInterviews();
	const live = useLiveTranscription();

	const [session, setSession] = React.useState<SessionPhase>({ phase: 'checking-permission' });

	const itemIdRef = React.useRef<string | null>(null);
	/** Captured the instant `start()` resolves the local interview, and read back by `stop()`: the
	 * reactive `localInterview` above is not a safe source there, since nothing guarantees the
	 * `.find()` still matches by the time an async capture session actually ends. */
	const subjectRef = React.useRef<SubjectAddress | null>(null);
	const preparationIdRef = React.useRef<number | null>(null);
	const pendingChunksRef = React.useRef<CaptureChunk[]>([]);
	const rotationTimerRef = React.useRef<number | null>(null);
	/** The screen's own intent, distinct from the adapter's `state`: the adapter can restart the
	 * recorder on its own (a recoverable interruption resolving while the screen is paused), and only
	 * this flag lets the pause-intent guard tell that restart from a real "Reprendre". */
	const pauseIntentRef = React.useRef(false);
	const unsubscribeChunkRef = React.useRef<(() => void) | null>(null);
	const unsubscribeFailureRef = React.useRef<(() => void) | null>(null);
	const unsubscribeSnapshotRef = React.useRef<(() => void) | null>(null);

	// Step 0 — the interview is resolved locally, never over the network: `OutboxRunnerHost` has
	// already started `LocalInterviewPort` for this session (`guides/state-stores.md` no longer
	// applies here — there is no query, no cache preset, nothing to `stopOn`). The microphone is
	// never opened until a local record is found and is still capturable.
	const isInterviewHydrated = localInterviewPort.isHydrated();
	const localInterview: LocalInterview | null = React.useMemo(
		() => localInterviews.find((candidate) => candidate.id === localInterviewId) ?? null,
		[localInterviews, localInterviewId],
	);

	const kind: InterviewKind | null =
		localInterview === null ? null : (localInterview.kind ?? InterviewKind.CONVERSATION);

	const isInterviewLoading = !isInterviewHydrated;
	const isInterviewNotFound = isInterviewHydrated && localInterview === null;
	const isAlreadyCaptured =
		localInterview !== null && localInterviewCaptureState(localInterview) === 'already-captured';
	// Gates the effects below: only a hydrated, found, still-capturable interview may open the
	// microphone. Deliberately not reused once `session.phase` has left `'checking-permission'` — see
	// the `state` fold at the bottom, which explains why `isAlreadyCaptured` must never override a
	// session already under way.
	const canCapture = isInterviewHydrated && localInterview !== null && !isAlreadyCaptured;

	const clearRotationTimer = React.useCallback(() => {
		if (rotationTimerRef.current !== null) {
			window.clearInterval(rotationTimerRef.current);
			rotationTimerRef.current = null;
		}
	}, []);

	const scheduleRotationTimer = React.useCallback(() => {
		clearRotationTimer();
		rotationTimerRef.current = window.setInterval(() => {
			// Guard 2 (§8 étape 7): even if this timer fired while a pause was in flight, rotating is
			// refused unless the capture is actually `'recording'` — the adapter itself accepts
			// `'paused'` and would restart the microphone.
			if (capturePort.snapshot().state === 'recording') {
				capturePort.rotateSegment();
			}
		}, SEGMENT_ROTATION_MS);
	}, [capturePort, clearRotationTimer]);

	const cleanupActiveCapture = React.useCallback(() => {
		clearRotationTimer();
		unsubscribeChunkRef.current?.();
		unsubscribeChunkRef.current = null;
		unsubscribeFailureRef.current?.();
		unsubscribeFailureRef.current = null;
		unsubscribeSnapshotRef.current?.();
		unsubscribeSnapshotRef.current = null;
		itemIdRef.current = null;
		subjectRef.current = null;
		preparationIdRef.current = null;
		pendingChunksRef.current = [];
		pauseIntentRef.current = false;
	}, [clearRotationTimer]);

	// Step 1 — screen kept awake for the whole session, regardless of permission/start outcome: the
	// adapter re-acquires on its own on return to foreground.
	React.useEffect(() => {
		if (!canCapture) {
			return undefined;
		}

		void screenWakePort.acquire();

		return () => {
			void screenWakePort.release();
		};
	}, [screenWakePort, canCapture]);

	// Step 2 — permission is only queried once the local interview is known capturable, and never
	// re-queried: a `'denied'`/`'unsupported'` answer blocks before any microphone access.
	React.useEffect(() => {
		if (!canCapture) {
			return undefined;
		}

		let isCancelled = false;

		void capturePort.queryPermission().then((permission) => {
			if (isCancelled) {
				return;
			}

			if (permission === 'denied') {
				setSession({ phase: 'permission-denied' });
				return;
			}

			if (permission === 'unsupported') {
				setSession({ phase: 'unsupported' });
				return;
			}

			setSession({ phase: 'ready' });
		});

		return () => {
			isCancelled = true;
		};
	}, [capturePort, canCapture]);

	const handleChunk = React.useCallback(
		(chunk: CaptureChunk) => {
			live.forward(chunk);

			// Step 5 — `itemId` is only known once `createItem` (a local write) settles, while chunks can
			// start arriving right after `start()`: anything received before that is queued here, in
			// order, and flushed in the same order once the id exists (see `start` below).
			if (itemIdRef.current === null) {
				pendingChunksRef.current.push(chunk);
				return;
			}

			void localBufferPort.appendChunk(itemIdRef.current, chunk.segment, chunk.index, chunk.blob);
		},
		[localBufferPort, live],
	);

	const handleFailure = React.useCallback(
		(failure: CaptureFailure) => {
			live.end();
			cleanupActiveCapture();
			setSession({ phase: 'terminal-failure', reason: mapFailureToMessage(failure) });
		},
		[cleanupActiveCapture, live],
	);

	const handleSnapshotChange = React.useCallback(
		(snapshot: CaptureSnapshot) => {
			// Step 8, second guard — the adapter can restart the recorder entirely on its own (a locked
			// screen, a backgrounded tab, an incoming call all resolve through the same recovery path) while
			// the screen still intends to stay paused. Calling `pause()` from `onInterruption` would be a
			// no-op (the recorder is `null` mid-interruption); this only re-asserts the pause once the
			// snapshot actually reports `'recording'` again.
			if (pauseIntentRef.current && snapshot.state === 'recording') {
				capturePort.pause();
				return;
			}

			if (snapshot.state !== 'recording' && snapshot.state !== 'paused') {
				return;
			}

			setSession({
				phase: snapshot.state,
				recordedMs: snapshot.recordedMs,
				level: snapshot.level,
				interruptionCount: snapshot.interruptionCount,
			});
		},
		[capturePort],
	);

	// Refreshes the recorded duration and level between real transitions: `capture.subscribe` only
	// fires on a state change, never on the mere passing of time. While paused the port keeps
	// `recordedMs` frozen, so the tick leaves the displayed duration unchanged.
	React.useEffect(() => {
		if (session.phase !== 'recording' && session.phase !== 'paused') {
			return undefined;
		}

		const timer = window.setInterval(() => {
			const snapshot = capturePort.snapshot();

			setSession((current) => {
				if (current.phase !== 'recording' && current.phase !== 'paused') {
					return current;
				}

				return {
					phase: current.phase,
					recordedMs: snapshot.recordedMs,
					level: snapshot.level,
					interruptionCount: snapshot.interruptionCount,
				};
			});
		}, ELAPSED_TICK_MS);

		return () => {
			window.clearInterval(timer);
		};
	}, [capturePort, session.phase]);

	const flushPendingChunks = React.useCallback(
		async (itemId: string): Promise<void> => {
			// Drains strictly in order, and keeps draining as long as new fragments keep landing in the
			// queue while this runs: `itemIdRef` is only set once this resolves, so a fragment captured
			// mid-flush is queued behind the ones already waiting rather than racing past them.
			while (pendingChunksRef.current.length > 0) {
				const nextChunk = pendingChunksRef.current.shift();
				if (nextChunk === undefined) {
					break;
				}

				await localBufferPort.appendChunk(itemId, nextChunk.segment, nextChunk.index, nextChunk.blob);
			}
		},
		[localBufferPort],
	);

	const start = React.useCallback(() => {
		if (session.phase !== 'ready' || ownerUserId === null || localInterview === null) {
			return;
		}

		// Re-checked here, right before opening the microphone, rather than trusted from an earlier
		// render: another session on this shared device may have attached a queue item to this same
		// local interview between the moment this screen reached `'ready'` and this click. Routing away
		// here routes to its upload page — the ambient `state` fold below never performs this redirect
		// once a session is under way, on purpose (see its comment), so this is the one place that
		// still can.
		if (localInterviewCaptureState(localInterview) === 'already-captured') {
			navigate(
				`${buildSubjectPath(localInterview.subject)}/local-interviews/${localInterview.id}/upload`,
			);
			return;
		}

		const resolvedLocalInterview = localInterview;
		const resolvedOwnerUserId = ownerUserId;
		subjectRef.current = resolvedLocalInterview.subject;
		preparationIdRef.current = resolvedLocalInterview.preparationId ?? null;

		setSession({ phase: 'starting' });

		// Asked again on every capture, on purpose, rather than remembered behind a flag: the call is
		// idempotent and free once already granted, and a prior refusal deserves to be asked again
		// (`LocalBufferPort.ts`). A rejection here never blocks the capture itself from starting.
		void localBufferPort.requestPersistence().catch(() => undefined);

		// Step 5 — registered before `start()` itself: chunks can start emitting as soon as the recorder
		// is actually running, well before `createItem` below has resolved.
		unsubscribeChunkRef.current = capturePort.onChunk(handleChunk);
		live.begin(resolvedLocalInterview);
		unsubscribeFailureRef.current = capturePort.onFailure(handleFailure);

		void (async () => {
			await capturePort.start({ timesliceMs: CAPTURE_TIMESLICE_MS });

			const startedSnapshot = capturePort.snapshot();
			if (startedSnapshot.state !== 'recording') {
				// `onFailure` already reported a terminal failure for this attempt; nothing more to do.
				return;
			}

			const mimeType = startedSnapshot.mimeType;
			if (mimeType === null) {
				await capturePort.stop();
				live.end();
				cleanupActiveCapture();
				setSession({ phase: 'format-unrecognized' });
				return;
			}

			try {
				const item = await outboxPort.createItem({
					kind: 'interview-recording',
					mimeType,
					// Technical identifiers only (`OutboxPort.ts`): never `participants`, never a name.
					context: {
						facilityId: resolvedLocalInterview.facilityId,
						subjectTypeKey: resolvedLocalInterview.subject.typeKey,
						subjectId: resolvedLocalInterview.subject.subjectId,
						localInterviewId: resolvedLocalInterview.id,
					},
					ownerUserId: resolvedOwnerUserId,
				});

				// Closes the reopening this predicate guards against — must happen right after
				// `createItem`, before anything else touches the buffer.
				await localInterviewPort.attachOutboxItem(resolvedLocalInterview.id, item.id);

				await flushPendingChunks(item.id);
				itemIdRef.current = item.id;

				pauseIntentRef.current = false;
				unsubscribeSnapshotRef.current = capturePort.subscribe(handleSnapshotChange);
				scheduleRotationTimer();

				const runningSnapshot = capturePort.snapshot();
				setSession({
					phase: runningSnapshot.state === 'paused' ? 'paused' : 'recording',
					recordedMs: runningSnapshot.recordedMs,
					level: runningSnapshot.level,
					interruptionCount: runningSnapshot.interruptionCount,
				});
			} catch {
				await capturePort.stop();
				live.end();
				cleanupActiveCapture();
				setSession({ phase: 'terminal-failure', reason: ITEM_PREPARATION_FAILURE_MESSAGE });
			}
		})();
	}, [
		session.phase,
		ownerUserId,
		localInterview,
		navigate,
		capturePort,
		localBufferPort,
		outboxPort,
		localInterviewPort,
		handleChunk,
		live,
		handleFailure,
		handleSnapshotChange,
		flushPendingChunks,
		scheduleRotationTimer,
		cleanupActiveCapture,
	]);

	const pause = React.useCallback(() => {
		if (session.phase !== 'recording') {
			return;
		}

		// Step 8, first guard — suspends the rotation timer so it can never fire while paused (guard 2
		// in `scheduleRotationTimer` covers the case where it fires anyway).
		pauseIntentRef.current = true;
		capturePort.pause();
		clearRotationTimer();
	}, [session.phase, capturePort, clearRotationTimer]);

	const resume = React.useCallback(() => {
		if (session.phase !== 'paused') {
			return;
		}

		pauseIntentRef.current = false;
		capturePort.resume();
		scheduleRotationTimer();
	}, [session.phase, capturePort, scheduleRotationTimer]);

	const stop = React.useCallback(() => {
		if (session.phase !== 'recording' && session.phase !== 'paused') {
			return;
		}

		const currentItemId = itemIdRef.current;
		if (currentItemId === null) {
			return;
		}

		const targetSubject = subjectRef.current;
		const targetPreparationId = preparationIdRef.current;
		const isNote = localInterview?.kind === InterviewKind.NOTES;

		setSession({ phase: 'stopping' });

		void (async () => {
			// Read before `stop()` so the teardown latency is not counted; the stored `durationMs` is the
			// recorded time, pauses excluded.
			const durationMs = capturePort.snapshot().recordedMs;

			await capturePort.stop();
			live.end();
			cleanupActiveCapture();

			try {
				await outboxPort.markReady(currentItemId, { durationMs });
			} catch {
				// `markReady` only throws when the runner never started, or the item already left
				// `'draft'` — both describe a state this screen cannot repair; the transfer either
				// already happened or never can, and there is nothing left to retry from here.
			}

			// `targetSubject` was captured in `subjectRef` when `start()` resolved the local
			// interview: reaching `'recording'`/`'paused'` in the first place guarantees it is set. An
			// interview captured for a preparation goes back to it, where the element shows up as it is
			// sent and transcribed; any other goes to the processing follow-up, keyed by the local
			// interview's own id — the client reference, stable from before the queue item exists to after
			// the whole chain completes.
			if (targetSubject !== null) {
				if (targetPreparationId !== null) {
					toast.current?.show({
						severity: 'info',
						summary: isNote
							? 'Note ajoutée : transcription en cours. Rien n’est généré.'
							: 'Entretien ajouté : transcription en cours. Rien n’est généré.',
					});
				}

				navigate(
					targetPreparationId !== null
						? `${buildSubjectPath(targetSubject)}/preparations/${targetPreparationId}`
						: `${buildSubjectPath(targetSubject)}/local-interviews/${localInterviewId}/upload`,
				);
			}
		})();
	}, [
		session.phase,
		capturePort,
		outboxPort,
		cleanupActiveCapture,
		live,
		navigate,
		toast,
		localInterview,
		localInterviewId,
	]);

	// Unmount: only ever releases what this screen itself acquired (listeners, timer). Never calls
	// `capturePort.stop()` nor `outboxPort.discard()` here: an item left as a `draft` with no capture
	// running is presented to the user as interrupted, who then chooses to send or delete it.
	React.useEffect(() => {
		return () => {
			cleanupActiveCapture();
		};
	}, [cleanupActiveCapture]);

	// Folds the local-resolution outcome into `state` only while the session has not left
	// `'checking-permission'` yet. Past that point `session` alone drives the screen: once `start()`
	// has attached a queue item to this local interview (step 5, above), `localInterview` itself
	// starts reporting `'already-captured'` for the very session that produced it — reusing
	// `isAlreadyCaptured` past this point would wrongly redirect a capture that is legitimately under
	// way. The one place that still needs to catch a genuine race after `'ready'` is the explicit
	// re-check inside `start()` itself.
	const state: InterviewCaptureScreenState =
		session.phase === 'checking-permission'
			? isInterviewLoading
				? { phase: 'loading-interview' }
				: isInterviewNotFound
					? { phase: 'interview-not-found' }
					: isAlreadyCaptured
						? { phase: 'already-captured' }
						: session
			: session;

	return {
		state,
		kind,
		preparationId: localInterview?.preparationId ?? null,
		start,
		pause,
		resume,
		stop,
	};
}

export default useInterviewCapture;
